<template>
  <LazyCommonAppHeaderDiscountCompaign
    v-if="discountCampaignTopBanner && !isCheckout"
    ref="headerDiscountEl"
    :content="discountCampaignTopBanner.top_bar_text"
    class="z-[998]"
    :class="[defaultState ? 'border-white' : 'border-primary']"
  />
  <header
    ref="headerEl"
    class="sticky top-0 z-[998] w-full"
    :class="[isTopTransparentState ? 'h-0' : 'h-full']"
  >
    <CommonAppHeaderSeoNav class="visually-hidden" />
    <div
      ref="headerTopEl"
      class="relative border-b"
      :class="[
        defaultState
          ? 'bg-primary border-white'
          : 'bg-transparent border-transparent',
        { 'max-lg:bg-primary': isOpen },
      ]"
    >
      <div class="container h-14 lg:h-16">
        <div
          class="flex h-full items-center gap-x-5 justify-between lg:gap-x-12.5"
          :class="{ 'lg:after:flex-1': isCheckout }"
        >
          <div v-if="!isCheckout" class="flex flex-1 gap-5 py-4">
            <UiButton
              variant="clear"
              variant-type="icon"
              size="xs"
              class="lg:hidden text-white"
              data-menu-toggle
              aria-label="side navigation"
              @click="openBurgerMenu"
            >
              <UiIcon
                aria-hidden="true"
                :name="isOpen ? 'close' : 'menu'"
                :class="isOpen ? 'w-6' : 'w-5'"
              />
            </UiButton>
            <div v-if="!isCheckout" ref="headerNavEl">
              <LazyCommonAppHeaderNav
                class="hidden lg:flex items-center gap-x-4"
                @update:activeMenu="activeMenuHandler"
              />
            </div>
          </div>
          <div v-if="isCheckout" class="flex-1 hidden lg:flex">
            <nuxt-link
              :to="
                localePath(`/cart/${generalStore.regularCart?.unique_id || ''}`)
              "
              class="items-center gap-1.5 text-white inline-flex"
            >
              <UiIcon name="arrow_left" class="size-4" />
              {{ $t("returnToBag") }}
            </nuxt-link>
          </div>
          <div class="flex" :class="{ 'mx-auto': isCheckout }">
            <nuxt-link
              :to="localePath('/')"
              class="max-w-full flex-shrink-0 inline-block"
              @click="closeBurgerMenu"
            >
              <UiLazyImg
                class="w-42 lg:w-48"
                :alt="
                  generalStore.settings?.company_name
                    ? `${generalStore.settings?.company_name} ${$t('headerDesktopLogo')}`
                    : 'Company logo'
                "
                :src="generalStore?.settings?.desktop_logo || ''"
                :src-mobile="generalStore?.settings?.mobile_logo"
                no-lazy
              />
            </nuxt-link>
          </div>

          <div
            v-if="!hideMenu"
            class="flex flex-1 items-center gap-6 justify-end"
          >
            <div class="flex items-center justify-end gap-x-1 py-4 lg:gap-x-6">
              <UiButton
                variant="clear"
                size="xs"
                aria-label="global-search"
                class="flex text-white p-0 border-0"
                @click.prevent="toggleSearch"
              >
                <UiIcon aria-hidden="true" name="search" class="size-6" />
              </UiButton>
              <UiButton
                variant="clear"
                size="xs"
                class="max-lg:hidden hover:underline text-white p-0 border-0"
                aria-label="wishlist"
                @click="openWishlist"
              >
                <span class="ui-text-m normal-case">
                  {{ $t("wishlist") }}
                </span>
              </UiButton>
              <nuxt-link
                v-if="generalStore.isAuthenticated"
                class="ui-text-m max-lg:hidden hover:underline text-white"
                :to="localePath('/profile')"
                @click="closeBurgerMenu"
              >
                {{ $t("account") }}
              </nuxt-link>
              <nuxt-link
                v-else
                class="ui-text-m max-lg:hidden hover:underline text-white"
                :to="localePath('/sign-in')"
                @click="goToSignIn"
              >
                {{ $t("login") }}
              </nuxt-link>
              <UiButton
                variant="clear"
                size="xs"
                class="flex text-white p-0 border-0 lg:hover:underline"
                aria-label="cart"
                @click="openCart"
              >
                <span class="relative block lg:hidden">
                  <UiIcon aria-hidden="true" name="cart" class="size-6" />
                  <ClientOnly>
                    <span
                      v-if="cartItemsQuantity"
                      class="absolute left-0 top-[9px] justify-center w-full pr-[1px] flex ui-text-3xs font-normal normal-case"
                    >
                      {{ cartItemsQuantity }}
                    </span>
                  </ClientOnly>
                </span>
                <span class="max-lg:hidden ui-text-m font-normal normal-case">
                  {{ $t("cart") }}
                  <ClientOnly>
                    <sup v-if="cartItemsQuantity" class="-top-1">
                      {{ cartItemsQuantity }}
                    </sup>
                  </ClientOnly>
                </span>
              </UiButton>
            </div>
          </div>
        </div>
      </div>
      <div class="drawer-wrap">
        <LazyCartDrawer />
        <LazyProfileWishlistDrawer />
        <LazyCommonAppHeaderSearchDrawer />
        <CommonAppHeaderBurgerModalLanding
          v-model="isOpen"
          :logo-src="generalStore?.settings?.desktop_logo ?? ''"
        />
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import type { MaybeElement } from "@vueuse/core";

const localePath = useLocalePathPolyfill();
const generalStore = useGeneralStore();
const checkoutStore = useCheckoutStore();
const route = useRoute();
const router = useRouter();
const { cartItemsQuantity } = useCart();

const searchedData = ref<string>("");
const headerEl = ref<MaybeElement>();
const headerTopEl = ref<MaybeElement>();
const headerNavEl = ref<MaybeElement>();
const headerDiscountEl = ref<MaybeElement>();
const isOpen = ref(false);
const isScrolled = ref<boolean>(false);
const previousScrollY = ref<number>(0);
const activeMenu = ref<boolean>(false);

const transparentMenuPages = ["/"];
const { height } = useElementSize(headerEl);
const { height: headerNavHeight } = useElementSize(headerNavEl);
const { height: headerTopHeight } = useElementSize(headerTopEl);
const { height: headerDiscountHeight } = useElementSize(headerDiscountEl);

watch(headerTopHeight, () => {
  setCustomProperty("--header-height", `${headerTopHeight.value}px`);
  setCustomProperty(
    "--header-top-height",
    `${headerTopHeight.value ? headerTopHeight.value : 0}px`,
  );
  setCustomProperty("--header-nav-height", `${headerNavHeight.value}px`);
});

const isCheckout = computed(() => {
  return (
    route.path.includes("checkout") && !route.path.includes("checkout/success")
  );
});

const isCart = computed(() => {
  return route.path.includes("cart");
});
const hideMenu = computed(() => {
  return isCheckout.value;
});

const discountCampaignTopBanner = computed(() => {
  return generalStore.discountCampaignsData.topBanner;
});

const isTopTransparentState = computed(() => {
  return transparentMenuPages.includes(router.currentRoute.value.path); // Problem with route.path, doesn't always support reactivity in this case
});

const defaultState = computed(() => {
  return (
    !isTopTransparentState.value ||
    isScrolled.value ||
    Boolean(activeMenu.value) ||
    isOpen.value
  );
});

onMounted(() => {
  window.addEventListener("scroll", handleScroll);
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", handleScroll);
});

watch(searchedData, async (val) => {
  const el = document?.querySelector("body");
  if (!el) {
    return;
  }
  window.scrollTo(0, 0);
  await nextTick();

  val ? (el.style.overflowY = "hidden") : el.style.removeProperty("overflow-y");
});

function handleScroll() {
  const currentScrollY = window.scrollY;
  const isScrollingUp = currentScrollY < previousScrollY.value;

  if (isScrollingUp) {
    isScrolled.value =
      currentScrollY > headerDiscountHeight.value + headerTopHeight.value;
  } else {
    isScrolled.value = currentScrollY > headerDiscountHeight.value;
  }

  previousScrollY.value = currentScrollY;
}

function openBurgerMenu() {
  isOpen.value = !isOpen.value;
}

function openWishlist() {
  checkoutStore.isCartOpen = false;
  closeBurgerMenu();
  generalStore.isWishlistDrawerOpen = !generalStore.isWishlistDrawerOpen;
  generalStore.isSearchDrawerOpen = false;
}

function toggleSearch() {
  closeBurgerMenu();
  generalStore.isSearchDrawerOpen = !generalStore.isSearchDrawerOpen;
}

function setSearchLength(data: string) {
  searchedData.value = data;
}

function closeBurgerMenu() {
  isOpen.value = false;
}

function goToSignIn() {
  generalStore.isWishlistDrawerOpen = false;
  checkoutStore.isCartOpen = false;
  closeBurgerMenu();
}

function openCart() {
  generalStore.isWishlistDrawerOpen = false;
  closeBurgerMenu();
  checkoutStore.isCartOpen = !checkoutStore.isCartOpen;
  generalStore.isSearchDrawerOpen = false;
}

function activeMenuHandler(value: boolean) {
  activeMenu.value = value;
}
</script>
